/**
 * Audit Trail panel for the candidate vault's "audit_trail" tab.
 *
 * Fetches /api/audit-logs (per-user) and renders a clean timeline.
 * Also exposes a working GDPR Article 15 "Download my data" button
 * that hits /api/me/export and triggers a JSON download.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  data_export: 'Downloaded GDPR Article 15 data export',
  access_link_created: 'Created a vault share link',
  access_link_revoked: 'Revoked a vault share link',
  vault_view: 'A recruiter opened your vault',
  ai_profile_generated: 'AI profile draft generated',
  ai_profile_approved: 'AI profile approved by you',
  ai_profile_unapproved: 'AI profile un-approved',
  document_uploaded: 'Document uploaded to vault',
  document_deleted: 'Document removed from vault',
  compliance_document_uploaded: 'Compliance pack document uploaded',
  compliance_document_deleted: 'Compliance pack document removed',
  employment_added: 'Employment record added',
  employment_updated: 'Employment record updated',
  employment_deleted: 'Employment record removed',
  vault_access_requested: 'Recruiter requested vault access',
  vault_access_approved: 'You approved a vault access request',
  vault_access_declined: 'You declined a vault access request',
  ai_overview_shared: 'AI overview shared to a requisition',
  password_changed: 'Password changed',
  mfa_enabled: 'MFA enabled',
  mfa_disabled: 'MFA disabled',
};

function fmt(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function AuditTrailPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/audit-logs`);
        if (alive) setEvents(Array.isArray(data) ? data : []);
      } catch (e) {
        if (alive) setError('Could not load the audit log.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const handleExport = async () => {
    setDownloading(true);
    try {
      const { data } = await axios.get(`${API}/me/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vault-hire-data-export-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Export failed. Please try again or contact support.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="vault-audit-trail">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl bg-[var(--vh-bg-muted)] border border-[var(--vh-border)]">
        <div>
          <p className="text-sm font-medium text-[var(--vh-text-primary)]">UK GDPR Article 15 — Subject Access</p>
          <p className="text-xs text-[var(--vh-text-secondary)]">Download every piece of data we hold about you, in clean JSON.</p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white text-sm font-medium transition disabled:opacity-60"
          data-testid="vault-dsar-download"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {downloading ? 'Preparing…' : 'Download my data'}
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-300 text-sm text-red-800" data-testid="vault-audit-error">{error}</div>
      )}

      <div>
        <h4 className="font-display text-lg text-[var(--vh-text-primary)] mb-3">Recent activity</h4>
        {loading ? (
          <p className="text-sm text-[var(--vh-text-muted)]" data-testid="vault-audit-loading">Loading audit log…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-[var(--vh-text-muted)]" data-testid="vault-audit-empty">No activity recorded yet. Every action in your vault is logged here.</p>
        ) : (
          <ul className="space-y-2" data-testid="vault-audit-list">
            {events.slice(0, 50).map((ev, idx) => {
              const label = ACTION_LABELS[ev.action] || ev.action.replace(/_/g, ' ');
              return (
                <li
                  key={ev.id || `${ev.action}-${idx}`}
                  className="flex items-start gap-3 p-3 rounded-lg bg-[var(--vh-bg-surface)] border border-[var(--vh-border)]"
                  data-testid={`vault-audit-event-${idx}`}
                >
                  <span className="mt-1 w-2 h-2 rounded-full bg-[var(--vh-accent)] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[var(--vh-text-primary)]">{label}</p>
                    <p className="text-xs text-[var(--vh-text-muted)] mt-0.5">{fmt(ev.timestamp)} · IP {ev.ip_address || 'n/a'}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
